<script setup lang="ts">
import { i18n } from '@/i18n';
import OverviewSectionCard from '../components/OverviewSectionCard.vue';
import { DASHBOARD_GENERAL_SECTION_ITEM, FILTER_OPTIONS, INVENTORY_DASHBOARD_PATH } from '../constants';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import SelectInput from '@/corePagesModule/SelectInput/SelectInput.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import SegmentControl from '@/corePagesModule/components/SegmentControl.vue';
import { computed, onMounted } from 'vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import ListViewSection from '../components/ListViewSection.vue';
import { storeToRefs } from 'pinia';
import { useInventoryDashboardStore } from './useInventoryDashboardStore';
import { ViewType } from './useInventoryDashboardStore/types';
import { InventoryDashboardViewModel } from './inventoryDashboardViewModel';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { useRouter } from 'vue-router';
import GraphViewSection from '../components/GraphViewSection.vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import EzTableLoadingState from '@/corePagesModule/components/ezTable/EzTableLoadingState.vue';
import EzTableErrorState from '@/corePagesModule/components/ezTable/EzTableErrorState.vue';
import { MetricTypes } from '../domains/entities/Dashboard';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';

const t = i18n.t;
const router = useRouter();

const store = useInventoryDashboardStore();
const { state } = storeToRefs(store);
const {
  changeDisplayYearMonth,
  changeDisplayYearMonthPeriod,
  changeForecastAccuracyPeriod,
  changeDimensionType,
  changeMetricType,
  getOverviewData,
  getByDimensionData,
} = store;
const vm = new InventoryDashboardViewModel();

const isInformationNotSelected = computed(() => vm.isDisplayDefaultView(state.value.information));
const locale = computed(() => i18n.locale);

/**
 * KPI設定ボタン押下時ハンドラ
 */
const HandleOnClickKpiSetting = () => {
  router.push(`${INVENTORY_DASHBOARD_PATH}/setting/kpi`);
};

/**
 * フィルター適用時のハンドラ
 * @param changedFilterItems - 変更したフィルタ情報
 */
const onApplyFilterHandler = async (changedFilterItems: FilterContentItem[]) => {
  state.value.filterItems = [...changedFilterItems];
  await getByDimensionData();
};

/**
 * ページタイプを変更した際の処理
 * @param value - 変更後のページタイプ
 */
const onChangeViewSegment = (value: string) => {
  state.value.checkedBusinessId = [];
  state.value.nowViewType = value as ViewType;
};

onMounted(async () => {
  state.value.checkedBusinessId = [];
  await waitLoading(getOverviewData(), undefined, '');
  await getByDimensionData();
});
</script>
<template>
  <div class="page-wrap">
    <div class="header">
      <div class="header__title">
        {{ t('ダッシュボード') }}
      </div>
      <TextButton size="btn-sm" @click="HandleOnClickKpiSetting">{{ t('KPI設定') }}</TextButton>
    </div>
    <div class="body">
      <div class="overview-section">
        <template v-for="item in DASHBOARD_GENERAL_SECTION_ITEM" :key="item.label">
          <OverviewSectionCard :item="item" />
        </template>
      </div>
      <div class="custom-section">
        <div class="custom-section__filter">
          <SelectInput
            :index="0"
            :model-value="t(vm.dimensionTypesMapper[state.information.dimensionType] ?? '')"
            :placeholder="t('選択してください')"
            filter-type="INVENTORY_DASHBOARD_DISPLAY_DATA_TYPE"
            :prop-options="vm.getDimensionTypesDisplayValue()"
            :use-search-input="false"
            :use-reset="false"
            :label="t('表示項目')"
            @update:model-value="(value) => changeDimensionType(vm.convertDisplayValueToDimensionType(value))"
          />
          <SelectInput
            :index="1"
            :model-value="t(vm.metricTypesMapper[state.information.metricType] ?? '')"
            :placeholder="t('選択してください')"
            filter-type="INVENTORY_DASHBOARD_DISPLAY_DATA_COLUMN"
            :prop-options="vm.getMetricTypesDisplayValue()"
            :use-search-input="false"
            :use-reset="false"
            :label="t('表示データ')"
            @update:model-value="(value) => changeMetricType(vm.convertDisplayValueToMetricType(value))"
          />
          <SelectInput
            :index="2"
            :model-value="vm.convertNumberToDisplayForecastAccuracyPeriod(state.forecastAccuracyPeriod)"
            :placeholder="t('選択してください')"
            :prop-options="FILTER_OPTIONS.ANNOUNCE_ACCURACY.map((option) => t(option))"
            filter-type="INVENTORY_DASHBOARD_ANNOUNCE_ACCURACY"
            :use-search-input="false"
            :use-reset="true"
            :label="t('考慮する内示正答率期間')"
            :disabled="state.information.metricType !== MetricTypes.Turnover"
            @update:model-value="(value) => changeForecastAccuracyPeriod(vm.convertDisplayForecastAccuracyPeriodToNumber(value))"
          />
          <FilterModalButton filter-modal-type="inventoryDashboard" :select-input-index="10" @apply="onApplyFilterHandler" />
        </div>
        <div class="custom-section__display">
          <div class="selector">
            <div class="month-selector">
              <div class="month-selector__text">{{ t('表示月') }}</div>
              <div class="month-selector__selector">
                <MonthPicker
                  class="month-selector__input"
                  :locale="locale"
                  :prop-format="t('MonthPickerFormat')"
                  :model-value="state.displayYearMonth"
                  @update:model-value="changeDisplayYearMonth"
                />
                <Mdi class="month-selector__icon" icon="calendar_today" />
              </div>
            </div>
            <div v-if="state.nowViewType === ViewType.Graph" class="period-selector">
              <div class="period-selector__text">{{ t('表示期間') }}</div>
              <MonthPicker
                class="period-selector__input"
                :locale="locale"
                :prop-format="t('MonthPickerFormat')"
                :max-date="
                  DateService.getShiftedIYearMonthByMonths(state.displayYearMonthPeriod[1].year, state.displayYearMonthPeriod[1].month, -1)
                "
                :model-value="state.displayYearMonthPeriod[0]"
                @update:model-value="(e) => changeDisplayYearMonthPeriod([e, undefined])"
              />
              <div class="period-selector__text period-selector__text--line">~</div>
              <MonthPicker
                class="period-selector__input"
                :locale="locale"
                :prop-format="t('MonthPickerFormat')"
                :min-date="
                  DateService.getShiftedIYearMonthByMonths(state.displayYearMonthPeriod[0].year, state.displayYearMonthPeriod[0].month, 1)
                "
                :model-value="state.displayYearMonthPeriod[1]"
                @update:model-value="(e) => changeDisplayYearMonthPeriod([undefined, e])"
              />
            </div>
          </div>
          <SegmentControl
            :model-value="state.nowViewType"
            :value-a="ViewType.Graph"
            :value-b="ViewType.List"
            :label-a="t('月次推移表示')"
            :label-b="t('リスト表示')"
            aria-label="表示切り替え"
            name="change-view"
            @update:model-value="onChangeViewSegment"
          />
        </div>
        <div v-if="isInformationNotSelected" class="default-view">
          <div class="default-view__icon-bg">
            <Mdi icon="assessment" variant="outlined" class="default-view__icon" />
          </div>
          <div class="default-view__text">
            <div class="default-view__text--title">{{ t('在庫管理ダッシュボード') }}</div>
            <div class="default-view__text--desc">
              {{ t('表示項目、表示データ、表示月を選択して、設定したKPI達成率の確認や月次推移などのデータを可視化できます。') }}
            </div>
          </div>
        </div>
        <template v-else>
          <EzTableLoadingState v-if="state.informationLoadingState.status.type === 'loading'" :message="t('データを読み込んでいます')" />
          <template v-else-if="state.informationLoadingState.status.type === 'success'">
            <ListViewSection v-show="state.nowViewType === ViewType.List" />
            <GraphViewSection v-show="state.nowViewType === ViewType.Graph" />
          </template>
          <EzTableErrorState v-else @retry="getByDimensionData" />
        </template>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.page-wrap {
  padding: 16px 16px 0 16px;
  height: 100%;
  *:not(.material-icons) {
    font-family: Noto Sans JP;
  }
  display: grid;
  grid-template-rows: repeat(2, fit-content(100%));
  gap: 16px;
}
.header {
  display: grid;
  grid-template-columns: max-content max-content;
  justify-content: space-between;
  align-items: center;
  &__title {
    color: $textDefault;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.23;
  }
}

.body {
  display: grid;
  grid-template-rows: repeat(2, fit-content(100%));
  gap: 16px;
}

.overview-section {
  display: grid;
  grid-template-columns: repeat(v-bind('DASHBOARD_GENERAL_SECTION_ITEM.length'), minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.custom-section {
  display: grid;
  gap: 24px;
  background-color: $semanticWhite;
  padding: 16px;
  border: 1px solid $borderActive;
  border-radius: 4px;
  box-shadow: 0px 0px 16px 0px #1e92ef80;
  &__filter {
    display: grid;
    grid-template-columns: repeat(3, 160px) max-content;
    align-items: end;
  }
  &__display {
    display: grid;
    grid-template-columns: auto auto;
    justify-content: space-between;
    align-items: center;
  }
}

.selector {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 16px;
}

.month-selector {
  display: grid;
  grid-template-columns: auto auto;
  gap: 8px;
  align-items: center;

  &__selector {
    padding-right: 8px;
    display: grid;
    grid-template-columns: auto auto;
    width: max-content;
    height: 32px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    align-items: center;
  }
  &__input {
    width: 115px;
  }
  &__icon {
    font-size: 18px;
  }
}

.period-selector {
  display: grid;
  grid-template-columns: repeat(4, max-content);
  gap: 8px;
  align-items: center;
  &__text {
    height: mix-content;
    &--line {
      font-size: 16px;
    }
  }
  &__input {
    display: grid;
    width: max-content;
    height: 32px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    align-items: center;
    width: 115px;
  }
}

.default-view {
  display: grid;
  justify-content: center;
  justify-items: center;
  grid-template-columns: max-content;
  gap: 24px;
  padding-bottom: 16px;
  &__icon-bg {
    display: grid;
    width: 80px;
    height: 80px;
    border-radius: 999px;
    background-color: $borderDefault;
    place-items: center;
  }
  &__icon {
    font-size: 60px;
    color: $textSecondary;
  }
  &__text {
    display: grid;
    gap: 8px;
    justify-items: center;

    font-weight: 700;
    &--title {
      color: $textDefault;
      font-size: 22px;
      line-height: 1.3;
    }
    &--desc {
      color: $textSecondary;
      font-size: 16px;
      line-height: 1;
    }
  }
}

.invisible {
  visibility: hidden;
}
</style>
<style lang="scss">
.month-selector__input input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
  text-align: center;
}

.period-selector__input input {
  font-family: Noto Sans JP;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  font-weight: 700;
  color: $textDefault;
  text-align: center;
}
</style>
